import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { get, isEmpty, noop } from 'lodash';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikContext, useFormik } from 'formik';
// Import Types
import type { TitleKeyProps } from 'types';
import type { SMSProviderFormFields, SMSProviderResponseDTO } from './types';
import { GroupedOption } from 'components/common/PopoverWithOptionList/types';
// Import Store
import { useAppSelector } from 'store';
import { selectPartnerDataSourceId } from 'store/slices/partnerSettings';
// Import Validation
import { getSMSProviderCECValidationSchema } from './validation';
// Import Services
import { AdministrationHttpService } from 'services/http';
import { Helpers } from 'services';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
import useAdminFullPartnersList from 'hooks/UseAdminFullPartnersList';
import { useSMSProviderCECFormInputs } from './hooks';
// Import Helpers
import { mapApiModelToFormData, mapFormDataToApiModel } from './helpers';
// Import Components
import { BusyLoader, ExtendedInput, ModuleTitle } from '@geneui/components';
import PageHeadline from 'components/common/PageHeadline';
import PopoverWithOptionList from 'components/common/PopoverWithOptionList';
import DynamicNestedFormBuilder from './components/DynamicNestedFormBuilder';
import SMSTestSendModal from './components/SMSTestSendModal';
// Import Enums
import { PageTypesEnum, PermissionEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
import { SMS_PROVIDER_FORM_DEFAULT_VALUES } from './constants';
// Import Styles
import 'assets/scss/administration.scss';
import styles from './smsProviderCECPage.module.scss';

const { mapToPartnerGroupsModel } = Helpers;

interface Props extends TitleKeyProps {
    mode: PageTypesEnum;
}

const SMSProviderCECPage: FC<Props> = ({ titleKey, mode = PageTypesEnum.create }) => {
    const { SmsProviderId } = useParams<{ SmsProviderId?: string }>();
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();
    const { hasPermission } = useHasPermission();
    const hasAdminPermission = !!hasPermission(PermissionEnum.CRMAdmin);

    const { adminFullPartnersList: fullPartnersList, isLoading: isFullPartnersListLoading } =
        useAdminFullPartnersList();
    const partnerDataSourceId = useAppSelector(selectPartnerDataSourceId);

    const { fieldsList } = useSMSProviderCECFormInputs({ mode, hasAdminPermission });

    const [smsProviderData, setSmsProviderData] = useState<SMSProviderResponseDTO | null>(null);
    const [isGetSMSProviderDataLoading, setIsGetSMSProviderDataLoading] = useState(false);
    const [partnersFullListGroupModel, setPartnersFullListGroupModel] = useState<GroupedOption[]>([]);

    const [isOpenTestSendModal, setIsOpenTestSendModal] = useState(false);

    const { getSMSProviderRequest, createSMSProviderRequest, updateSMSProviderRequest, testSendSMSProviderRequest } =
        useCancelableRequests({
            getSMSProviderRequest: AdministrationHttpService.getSMSProvider,
            createSMSProviderRequest: AdministrationHttpService.createSMSProvider,
            updateSMSProviderRequest: AdministrationHttpService.updateSMSProvider,
            testSendSMSProviderRequest: AdministrationHttpService.testSendSMSProvider,
        });

    const initialValues = useMemo(() => {
        if (smsProviderData) {
            return mapApiModelToFormData(smsProviderData, mode);
        }
        return SMS_PROVIDER_FORM_DEFAULT_VALUES;
    }, [mode, smsProviderData]);

    const validationSchema = useMemo(() => getSMSProviderCECValidationSchema(t), [t]);

    const formik = useFormik<SMSProviderFormFields>({
        initialValues,
        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: noop,
        validationSchema,
    });
    const {
        values,
        errors,
        touched,
        isValid,
        setFieldValue,
        handleChange,
        handleBlur,
        resetForm,
        validateForm,
        submitForm,
        handleSubmit,
    } = formik;

    useEffect(() => {
        if (SmsProviderId) {
            setIsGetSMSProviderDataLoading(true);
            doGetRequest(getSMSProviderRequest.request, {
                queryString: {
                    smsProviderId: SmsProviderId,
                },
                successCallback: (data) => {
                    if (data) {
                        setSmsProviderData(data);
                    }
                },
            })?.then(() => {
                setIsGetSMSProviderDataLoading(false);
            });
        }
    }, [SmsProviderId, doGetRequest, getSMSProviderRequest.request]);

    const doCustomValidation = useCallback(() => {
        validateForm(values);
        return !isValid;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]);

    const handleSave = useCallback(() => {
        return new Promise((resolve, reject) => {
            return submitForm()
                .then(() => {
                    const apiModel = mapFormDataToApiModel(values);

                    const requestBody: typeof apiModel & { SmsProviderId?: string } = {
                        ...apiModel,
                    };

                    if ([PageTypesEnum.edit, PageTypesEnum.clone].includes(mode)) {
                        requestBody.SmsProviderId = SmsProviderId;
                    }

                    if ([PageTypesEnum.create, PageTypesEnum.clone].includes(mode)) {
                        doPostRequest(createSMSProviderRequest.request, {
                            requestBody,
                            successCallback: resolve,
                            errorCallback: reject,
                        });
                    } else {
                        doPostRequest(updateSMSProviderRequest.request, {
                            requestBody,
                            successCallback: resolve,
                            errorCallback: reject,
                        });
                    }
                })
                .catch(reject);
        });
    }, [
        SmsProviderId,
        createSMSProviderRequest.request,
        doPostRequest,
        mode,
        submitForm,
        updateSMSProviderRequest.request,
        values,
    ]);

    const handleOpenTestSendModal = useCallback(() => {
        setIsOpenTestSendModal(true);
    }, []);

    const handleCloseTestSendModal = useCallback(() => {
        setIsOpenTestSendModal(false);
    }, []);

    const handleTestSend = useCallback(
        (testSendValues): Promise<string> => {
            return new Promise((resolve, reject) => {
                const apiModel = mapFormDataToApiModel(values);

                const requestBody: typeof apiModel & { SmsProviderId?: string } = {
                    ...apiModel,
                };

                if ([PageTypesEnum.edit, PageTypesEnum.clone].includes(mode)) {
                    requestBody.SmsProviderId = SmsProviderId;
                }

                doPostRequest(testSendSMSProviderRequest.request, {
                    queryString: {
                        to: testSendValues.MobilePhone,
                    },
                    requestBody,
                    successCallback: (_data, alertMessage) => {
                        resolve(alertMessage ?? l.Success);
                    },
                    errorCallback: reject,
                });
            });
        },
        [SmsProviderId, doPostRequest, mode, testSendSMSProviderRequest.request, values],
    );

    type FieldsConfigType = (typeof fieldsList)[number];
    type FieldsListType = FieldsConfigType['fields'] | FieldsConfigType['leftFields'] | FieldsConfigType['rightFields'];

    useEffect(() => {
        if (!isEmpty(fullPartnersList) && partnerDataSourceId !== undefined) {
            const partnerGroupsModelData = mapToPartnerGroupsModel(fullPartnersList, partnerDataSourceId);
            setPartnersFullListGroupModel(partnerGroupsModelData);
        }
    }, [fullPartnersList, partnerDataSourceId]);

    const renderFormInputs = (inputsList: FieldsListType) => {
        return inputsList?.map((input) => {
            const { type, fieldValueType, name, label, fields, disabled, shouldNotBeNested } = input;
            const title = t(label || l[name]);

            const value = get(values, name);
            const errorText = get(errors, name);
            const isTouched = get(touched, name);
            const isValid = isTouched ? !errorText : undefined;

            switch (type) {
                case 'virtualizedDropdown':
                    return (
                        <PopoverWithOptionList
                            data={partnersFullListGroupModel}
                            isLoading={isFullPartnersListLoading}
                            key={name}
                            onOptionSelect={(option) => setFieldValue(name, option.value)}
                            errorText={errorText}
                            isValid={isValid}
                            buttonSelectText={t(l.SelectPartner)}
                            columnTitles={[l.Partner, l.ID]}
                            initialValue={value}
                            listWidth={890}
                        />
                    );

                case 'json':
                    return (
                        <DynamicNestedFormBuilder
                            key={name}
                            formKey={name}
                            title={label}
                            fieldValueType={fieldValueType}
                            disabled={disabled}
                            shouldNotBeNested={shouldNotBeNested}
                        />
                    );

                case 'block':
                    return (
                        <div key={name} className={styles.fieldsBlock}>
                            {renderFormInputs(fields)}
                        </div>
                    );

                case 'nestedBlock':
                    return (
                        <div key={name} className={styles.fieldsNestedBlock}>
                            <ModuleTitle title={name} />
                            <div className={styles.fieldsWrapper}>{renderFormInputs(fields)}</div>
                        </div>
                    );

                case 'text':
                default:
                    return (
                        <ExtendedInput
                            {...input}
                            key={name}
                            title={title}
                            label={title}
                            placeholder={title}
                            value={value}
                            errorText={errorText}
                            isValid={isValid}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    );
            }
        });
    };

    return (
        <FormikContext.Provider value={formik}>
            <form
                className={classNames(styles.container, 'crm-administration-settings-main-wrapper')}
                onSubmit={handleSubmit}
            >
                <BusyLoader isBusy={isGetSMSProviderDataLoading} spinnerSize="big">
                    <PageHeadline
                        isLoading={isGetSMSProviderDataLoading}
                        titleKey={titleKey}
                        handleReset={resetForm}
                        handleSave={handleSave}
                        handleTestSend={handleOpenTestSendModal}
                        doCustomValidation={doCustomValidation}
                        isViewPage={false}
                        isDataReady={true}
                        isVisibleTitleAndDescriptionFields={false}
                        isTitleAndDescriptionEditable={false}
                        isVisibleTitleInModal={false}
                        isVisibleDescriptionInModal={false}
                        withWarningsAlertContent={true}
                        visibleLeavePageOption={true}
                    />
                    <div className={classNames(styles.wrapper)}>
                        <div className={classNames('crm-section crm-section-full-height', styles.crmSection)}>
                            {fieldsList.map(({ title, fields, leftFields, rightFields }, index) => (
                                <div
                                    key={index}
                                    className={classNames('section-holder', styles.sectionHolder, {
                                        [styles.twoColumns]: leftFields && rightFields,
                                    })}
                                >
                                    {title && <ModuleTitle title={t(l[title])} />}

                                    {fields && <div className={styles.sectionContent}>{renderFormInputs(fields)}</div>}

                                    {leftFields && rightFields && (
                                        <div className={styles.sectionContent}>
                                            <div className={styles.fieldsBlock}>{renderFormInputs(leftFields)}</div>
                                            <div className={styles.fieldsBlock}>{renderFormInputs(rightFields)}</div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </BusyLoader>
            </form>

            {isOpenTestSendModal && (
                <SMSTestSendModal handleClose={handleCloseTestSendModal} handleTestSend={handleTestSend} />
            )}
        </FormikContext.Provider>
    );
};

export default memo(SMSProviderCECPage);
